import { datadogRum } from '@datadog/browser-rum'
import { NodeViewWrapper, type ReactNodeViewProps } from '@tiptap/react'
import type { ComponentType, JSX } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

// What a node that cannot be shown is called in its place: the text it
// carries for readers, when it has one.
function nodeLabel(props: ReactNodeViewProps): string {
  const attrs = props.node.attrs
  for (const value of [
    attrs.codingDisplay,
    attrs.display,
    attrs.summary?.title,
    props.node.textContent,
  ]) {
    if (typeof value === 'string' && value.trim()) {
      return value.replace(/^@/, '')
    }
  }
  return 'Content'
}

export function NodeViewFallback({
  label,
  inline,
}: {
  label: string
  inline: boolean
}): JSX.Element {
  return (
    <NodeViewWrapper
      as={inline ? 'span' : 'div'}
      contentEditable={false}
      data-node-view-fallback=""
      className={inline ? 'inline' : 'my-1'}
    >
      <span
        title="This can't be shown in this version of Orbit"
        className="rounded-xs border border-dashed border-gray-450 px-1 text-muted-foreground"
      >
        {label}
      </span>
    </NodeViewWrapper>
  )
}

/* Contains a node view that fails to render (a note saved by a newer
 * version, say, holding something this one does not understand) to the
 * node: it shows as its label, and the rest of the note, and the page around
 * it, keep working. */
export function resilientNodeView(
  View: ComponentType<ReactNodeViewProps>,
  { inline }: { inline: boolean }
): ComponentType<ReactNodeViewProps> {
  function ResilientNodeView(props: ReactNodeViewProps): JSX.Element {
    return (
      <ErrorBoundary
        resetKeys={[props.node]}
        onError={(error) =>
          datadogRum.addError(error, {
            source: 'node-view',
            nodeType: props.node.type.name,
          })
        }
        fallbackRender={() => (
          <NodeViewFallback label={nodeLabel(props)} inline={inline} />
        )}
      >
        <View {...props} />
      </ErrorBoundary>
    )
  }
  ResilientNodeView.displayName = `Resilient(${View.displayName ?? View.name})`
  return ResilientNodeView
}
